import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
    FaArrowLeft, FaSync, FaFileInvoiceDollar, FaExclamationTriangle,
    FaTruck, FaFileAlt, FaTimes, FaWallet, FaBalanceScale, FaHashtag,
    FaPaperclip, FaUpload, FaDownload, FaTrash, FaPlane, FaFilePdf,
    FaFileExcel, FaSearch, FaCalendarAlt, FaEnvelope, FaUniversity,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import ErpTemplate from '../../../components/ErpTemplate';
import { FinField, FinInput } from '../../../components/FinField';
import ReactSelect from '../../../components/ReactSelect';
import api from '../../../services/api';
import { moeda } from '../../../util/util';
import { OrigemMoeda } from '../../../components/ValorComOrigem';
import { useSetFullBreadcrumbTrail } from '../../../context/BreadcrumbContext';
import '../../../css/financeiro-parcela-pagar.css';
import ChecklistFaturamento from './ChecklistFaturamento';

// Tela de CONFERÊNCIA do faturamento — o que a Simone abre ao clicar numa linha
// da fila (PainelFaturamento). Shell padrão do financeiro (breadcrumb +
// .fin-pp__head + KPIs + .fin-pp__region com tabela .fin-pp__tbl — modelo:
// financeiroParcelaPagar).
//
// Ordem dos blocos: resumo ND x NF (KPIs) -> pendências -> fornecedores ->
// documentos. Pendência vem cedo de propósito: é o que a Simone precisa ver
// antes de gerar o documento. Desde 27/08/2026 ela NÃO trava mais os botões
// "Gerar ND / Gerar NF" — avisa e deixa seguir.

const ROTULO_TIPO = { nd: 'ND', nf: 'NF', nao_informado: 'Não informado' };

// Anexos comprobatórios da ND (pedido do Rafael, 01/09/2026). Mesmo vocabulário
// do backend: `EventoFaturamentoArquivo::TIPOS`.
const TIPOS_ANEXO = [
    { value: 'recibo', label: 'Recibo' },
    { value: 'aereo', label: 'Aéreo' },
];

// Espelha `EventoFaturamentoArquivoController::EXTENSOES` e `MAX_KB`. PDF é o
// formato certo (o destino é um merge de PDFs), mas imagem entra porque recibo
// fotografado é a realidade — quem montar o PDF da ND converte.
const ACEITE_ANEXO = '.pdf,.jpg,.jpeg,.png';
const MAX_ANEXO_BYTES = 10 * 1024 * 1024;

// "2026-08-27 14:34:41" -> "27/08/2026 14:34" (sem depender de moment/timezone).
// Mesma decisão do PainelFaturamento — lá o helper é local ao arquivo, então
// repete aqui em vez de mexer naquela tela.
function dataHora(valor) {
    var texto = String(valor || '');
    if (texto.length < 10) return '—';
    var dia = texto.slice(8, 10) + '/' + texto.slice(5, 7) + '/' + texto.slice(0, 4);
    return texto.length >= 16 ? dia + ' ' + texto.slice(11, 16) : dia;
}

// `moeda` devolve "1.234,56" sem o "R$" — o prefixo entra no JSX.
// Helpers de data e e-mail portados do PreEnvioFaturamento — as duas telas leem o
// MESMO GET /orcamento/{id}/faturamento, então a Simone vê o que o operador viu.
// Estão duplicados de propósito: lá também são locais ao arquivo, e extrair para
// um util comum é refactor que não cabe nesta entrega.

// "2026-08-27 00:00:00" -> "27/08/2026". Devolve '' para o chamador decidir o
// texto do campo vazio.
function dataBr(valor) {
    var texto = String(valor || '');
    if (texto.length < 10) return '';
    return texto.slice(8, 10) + '/' + texto.slice(5, 7) + '/' + texto.slice(0, 4);
}

// `evento.hora`/`horaFim` são coluna `time` ("14:30:00"), mas aceita datetime por
// segurança — banco antigo tem registro dos dois jeitos.
function horaBr(valor) {
    var texto = String(valor || '');
    if (texto.length >= 16 && texto.indexOf(' ') > 0) return texto.slice(11, 16);
    return texto.length >= 5 ? texto.slice(0, 5) : '';
}

// Data e hora do evento são duas colunas separadas no banco.
function momento(data, hora) {
    var dia = dataBr(data);
    if (dia === '') return '';
    var h = horaBr(hora);
    return h === '' ? dia : dia + ' às ' + h;
}

// Lista de responsáveis ({id, nome}) em texto; `evento.responsavel` é CSV no banco.
function nomes(lista) {
    var pessoas = lista || [];
    return pessoas.map(function (pessoa) { return (pessoa && pessoa.nome) || ''; })
        .filter(Boolean)
        .join(', ');
}

// Mesma folga do backend (App\Classes\FaturamentoEmails): vírgula, ponto-e-vírgula
// ou quebra de linha separam; trim + lowercase; duplicado sai preservando a ordem.
function normalizarEmails(texto) {
    var pedacos = String(texto || '').split(/[,;\r\n]+/);
    var saida = [];
    for (var i = 0; i < pedacos.length; i++) {
        var email = pedacos[i].trim().toLowerCase();
        if (email === '') continue;
        if (saida.indexOf(email) !== -1) continue;
        saida.push(email);
    }
    return saida;
}

// De propósito mais frouxo que o FILTER_VALIDATE_EMAIL do PHP: o front nunca pode
// barrar endereço que o backend aceitaria. Quem recusa de verdade (422) é o backend.
function emailsInvalidos(emails) {
    var formato = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;
    return emails.filter(function (email) { return !formato.test(email); });
}

function reais(valor) {
    return 'R$ ' + moeda(valor);
}

function numero(valor) {
    var n = parseFloat(valor);
    return isNaN(n) ? 0 : n;
}

// { total, fornecedores } de uma faixa do resumo, tolerante a chave ausente.
function faixaResumo(resumo, chave) {
    var faixa = resumo && resumo[chave] ? resumo[chave] : null;
    return {
        total: faixa ? numero(faixa.total) : 0,
        fornecedores: faixa ? (parseInt(faixa.fornecedores, 10) || 0) : 0,
    };
}

function plural(qtd, singular, pluralizado) {
    return qtd + ' ' + (qtd === 1 ? singular : pluralizado);
}

// tipo nulo é "Não informado" — nunca ND. É pendência, não default.
function rotuloTipo(tipo) {
    return tipo && ROTULO_TIPO[tipo] ? ROTULO_TIPO[tipo] : ROTULO_TIPO.nao_informado;
}

// "9001/1" quando tem serie, "9001" quando nao. Vazio enquanto for previsto —
// o numero so existe depois que a prefeitura emite.
function numeroDocumento(d) {
    var n = String((d && d.numero) || '').trim();
    if (n === '') return '';
    var serie = String((d && d.serie) || '').trim();
    return serie !== '' ? n + '/' + serie : n;
}

function rotuloDocumento(d) {
    var tipo = String((d && d.tipo) || '');
    return ROTULO_TIPO[tipo] || tipo.toUpperCase() || '—';
}

function toneSituacao(situacao) {
    var s = String(situacao || '').toLowerCase();
    if (s === 'emitido') return ' is-ok';
    if (s === 'previsto') return ' is-warn';
    return '';
}

// Cabeçalho de bloco recolhível — o acordeão da casa (`.fin-pp__grp-head`), o
// mesmo do agrupamento por evento do financeiroParcelaPagar.
//
// O RESUMO (badge + total) mora no cabeçalho de propósito: com o bloco fechado
// ele ainda diz o que tem dentro ("6 fornecedores", "R$ 103.423,10"), então a
// Simone não abre só para descobrir. Era esse o pedido — a tela deixa de ser
// quilométrica sem esconder informação dela.
function BlocoHead(props) {
    return (
        <div className="fin-pp__grp-head"
            onClick={props.onToggle}
            title={props.aberto ? 'Recolher este bloco' : 'Abrir este bloco'}>
            <span className={'fin-pp__grp-caret' + (props.aberto ? ' is-open' : '')}>▶</span>
            <span className="fin-pp__grp-ic">{props.icone}</span>
            <div style={{ minWidth: 0, flex: 1 }}>
                {/* `h2` com a classe do grupo: mantém o título de seção que a
                    tela já tinha (o `.fin-pp__cphead-title` era um h2) sem sair
                    do visual do acordeão. */}
                <h2 className="fin-pp__grp-name" style={{ margin: 0 }}>{props.titulo}</h2>
                {props.subtitulo && (
                    <p className="fin-pp__cphead-sub" style={{ margin: '2px 0 0' }}>{props.subtitulo}</p>
                )}
            </div>
            {props.badge}
            {props.total && <span className="fin-pp__grp-total">{props.total}</span>}
            {/* Clique no botão de ação NÃO pode abrir/fechar o bloco — o
                "Anexar arquivo" mora aqui para continuar à mão mesmo com a
                lista recolhida. */}
            {props.acoes && (
                <span style={{ display: 'inline-flex', gap: 8, flex: 'none' }}
                    onClick={function (e) { e.stopPropagation(); }}>
                    {props.acoes}
                </span>
            )}
        </div>
    );
}

export default function DetalheFaturamento() {
    const { orcamentoId } = useParams();
    const history = useHistory();
    const token = localStorage.getItem('token');
    const setFullTrail = useSetFullBreadcrumbTrail();

    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    // Confirmação e erro são modal .fin-pp__modal controlado por estado — nunca
    // SweetAlert/Alert (não devolve Promise e destoa do padrão visual).
    const [tipoGerar, setTipoGerar] = useState(null);
    const [gerando, setGerando] = useState(false);
    // 2o tempo do fluxo: o documento nasce `previsto` e só vira `emitido` quando
    // a Simone volta da prefeitura com o número. `docEmitir` guarda a LINHA da
    // tabela (rótulo e valor entram no modal para ela conferir antes de emitir).
    const [docEmitir, setDocEmitir] = useState(null);
    const [numeroDoc, setNumeroDoc] = useState('');
    const [serieDoc, setSerieDoc] = useState('');
    const [emitindo, setEmitindo] = useState(false);
    // Anexos da ND: recibos e PDFs do aéreo que o Financeiro sobe aqui.
    const [anexoAberto, setAnexoAberto] = useState(false);
    const [anexoTipo, setAnexoTipo] = useState('recibo');
    const [anexoDescricao, setAnexoDescricao] = useState('');
    const [anexoFile, setAnexoFile] = useState(null);
    const [enviandoAnexo, setEnviandoAnexo] = useState(false);
    const [anexoRemover, setAnexoRemover] = useState(null);
    const [removendoAnexo, setRemovendoAnexo] = useState(false);
    // PDF da ND: `pdfGerando` guarda o ID do documento em geração (a tabela tem
    // várias linhas — um booleano só travaria os botões de todas).
    const [pdfGerando, setPdfGerando] = useState(null);
    // O que o backend não conseguiu pôr no PDF. Modal de AVISO, não de erro: o
    // PDF foi gerado e está lá. Guarda as DUAS listas — `itens` são campos da
    // carta que saíram em branco, `ignorados` são anexos que ficaram de fora do
    // merge. Uma coisa ela resolve no cadastro, a outra no arquivo; misturar as
    // duas numa lista só faria ela procurar no lugar errado.
    const [pdfFaltando, setPdfFaltando] = useState(null);
    // Excluir documento do evento: apagar para gerar de novo (Rafael, 03/09/2026).
    // `docExcluir` guarda a LINHA — o modal mostra rótulo e valor, que é o que
    // ela confere antes de confirmar.
    const [docExcluir, setDocExcluir] = useState(null);
    const [excluindo, setExcluindo] = useState(false);
    // Blocos recolhíveis. Começa com "Documentos do evento" ABERTO e os outros
    // dois fechados: é ali que estão os botões que ela vem apertar (informar
    // número, gerar PDF, excluir), e é a tabela mais curta — esconder a área de
    // ação custaria um clique toda visita sem encurtar nada. Fornecedores e
    // anexos são conferência: o resumo do cabeçalho já responde "o que tem
    // aqui", e quem quiser o detalhe abre.
    const [blocosAbertos, setBlocosAbertos] = useState({
        // Os quatro de conferência nascem FECHADOS: fechado é uma linha de
        // cabeçalho, então cabem todos sem empurrar a área de ação para longe.
        evento: false,
        po: false,
        cobranca: false,
        destinatarios: false,
        fornecedores: false,
        documentos: true,
        anexos: false,
    });
    // Filtro por nome do fornecedor. NÃO mexe no total do cabeçalho de propósito:
    // aquele número é o que ela cruza com a planilha do orçamento e não pode
    // mudar conforme o que está digitado na busca.
    const [filtroFornecedor, setFiltroFornecedor] = useState('');

    // Destinatários editáveis também aqui: a Simone é quem envia o faturamento ao
    // cliente. Se o operador esqueceu ou digitou errado, ela corrige sem trocar de
    // tela — o PUT é idempotente no carimbo de envio, salvar daqui não reenvia.
    const [emails, setEmails] = useState('');
    const [emailsSalvos, setEmailsSalvos] = useState('');
    const [salvandoEmails, setSalvandoEmails] = useState(false);

    // Mesmo PUT do envio: ele é idempotente no carimbo `enviadoFaturamento` e
    // aceita atualizar só os e-mails. Salvar daqui NÃO reenvia o orçamento.
    const salvarEmails = function () {
        if (salvandoEmails || emailsRuins.length > 0) { return; }
        setSalvandoEmails(true);
        api({
            method: 'put',
            action: '/orcamento/' + orcamentoId + '/enviarFaturamento',
            token: token,
            data: { emails: listaEmails },
        })
            .then(function () {
                toast.success('E-mails salvos.');
                setEmailsSalvos(listaEmails.join(', '));
            })
            .catch(function (err) {
                const d = (err && err.response && err.response.data) || {};
                toast.error(d.error || d.mensagem || 'Erro ao salvar os e-mails.');
            })
            .finally(function () { setSalvandoEmails(false); });
    };

    const alternarBloco = function (chave) {
        setBlocosAbertos(function (atual) {
            var novo = Object.assign({}, atual);
            novo[chave] = !novo[chave];
            return novo;
        });
    };

    useEffect(() => {
        setFullTrail([
            { path: '/financeiro', label: 'Financeiro' },
            { path: '/financeiroFaturamento', label: 'Faturamento' },
            { path: '/financeiroFaturamento/' + orcamentoId, label: 'Conferência' },
        ]);
    }, [setFullTrail, orcamentoId]);

    const buscar = useCallback(() => {
        setCarregando(true);
        api({
            method: 'get',
            action: '/orcamento/' + orcamentoId + '/faturamento',
            token: token,
        })
            .then(function (response) {
                const data = response.data || {};
                if (data.status === false) {
                    setDados(null);
                    setErro(data.error || 'Não foi possível carregar o faturamento.');
                    return;
                }
                setDados(data);
                setErro(null);
                // O campo nasce com o que o operador informou no envio; ela edita
                // por cima em vez de digitar de novo.
                setEmails((data.emails_faturamento || []).join(', '));
                setEmailsSalvos((data.emails_faturamento || []).join(', '));
            })
            .catch(function (err) {
                const d = (err && err.response && err.response.data) || {};
                setDados(null);
                setErro(d.error || d.mensagem || 'Erro ao carregar o faturamento.');
                toast.error('Erro ao carregar o faturamento.');
            })
            .finally(function () {
                setCarregando(false);
            });
    }, [orcamentoId, token]);

    useEffect(() => {
        buscar();
    }, [buscar]);

    const resumo = (dados && dados.resumo) || {};
    const fornecedores = (dados && dados.fornecedores) || [];
    const evento = (dados && dados.evento) || {};
    const datas = evento.datas || {};
    const po = (dados && dados.po) || {};
    const cobranca = (dados && dados.cobranca) || {};
    const contaCobranca = cobranca.conta || null;
    const inicioEvento = momento(datas.data, datas.hora);
    const fimEvento = momento(datas.dataFim, datas.horaFim);
    const replanejadoInicio = dataBr(datas.replanejadoInicio);
    const replanejadoFim = dataBr(datas.replanejadoFim);
    const fechamentoEvento = dataBr(datas.dataFechamento);
    const previsaoFaturamento = dataBr(datas.previsaoFaturamento);
    const responsaveisAccess = nomes(evento.responsavel);
    const respCliente = (evento.responsavelcliente && evento.responsavelcliente.nome) || '';
    const diretorResp = (evento.diretorresponsavel && evento.diretorresponsavel.nome) || '';
    const listaEmails = normalizarEmails(emails);
    const emailsRuins = emailsInvalidos(listaEmails);
    const emailsAlterados = listaEmails.join(', ') !== emailsSalvos;

    const vazio = <span className="fin-pp__cell-muted">Não informado</span>;

    // Linha rótulo/valor — o visual do rótulo vem do modificador .fin-pp__tbl--kv.
    const Linha = ({ label, children }) => (
        <tr>
            <td>{label}</td>
            <td style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{children}</td>
        </tr>
    );

    const documentos = (dados && dados.documentos) || [];
    const impeditivas = (dados && dados.pendencias && dados.pendencias.impeditivas) || [];
    const alertas = (dados && dados.pendencias && dados.pendencias.alertas) || [];
    // D-0056: fotografia do Aprovar Cliente × orçamento de agora. Só alerta — nada aqui
    // muda valor, total ou botão; null = a API não conseguiu comparar (não mostra nada).
    const fotografia = (dados && dados.fotografia) || null;
    const fotografiaMudou = !!fotografia && fotografia.situacao === 'mudou';
    const fotografiaDiferencas = (fotografia && fotografia.diferencas) || [];
    const adiantamento = (dados && dados.adiantamento) || {};
    const composicao = adiantamento.composicao || null;
    // Anexos comprobatórios da ND, já vêm no `show()` — sem 2ª chamada.
    const anexos = (dados && dados.arquivos_faturamento) || [];
    const aereo = (dados && dados.aereo) || {};
    const trechosAereo = parseInt(aereo.trechos, 10) || 0;

    // Filtro por nome. `indexOf` em vez de `includes` para acompanhar o resto do
    // arquivo (ES5 puro, sem depender de polyfill do CRA).
    const termoFornecedor = filtroFornecedor.trim().toLowerCase();
    const fornecedoresFiltrados = termoFornecedor === ''
        ? fornecedores
        : fornecedores.filter(function (f) {
            return String(f.fornecedor_nome || '').toLowerCase().indexOf(termoFornecedor) !== -1;
        });

    // Resumo do cabeçalho fechado de "Documentos do evento". Somar ND com NF
    // daria um total sem significado (são cobranças de naturezas diferentes do
    // mesmo evento) — a contagem por situação é o que ela precisa saber sem abrir.
    const documentosEmitidos = documentos.filter(function (d) {
        return String(d.situacao || '').toLowerCase() === 'emitido';
    }).length;
    const documentosPrevistos = documentos.filter(function (d) {
        return String(d.situacao || '').toLowerCase() === 'previsto';
    }).length;
    // Recebido x em aberto — o KPI mostrava um número só e não dizia se o
    // dinheiro tinha entrado. Em 27/08/2026 a Simone escreveu no faturamento
    // enviado ao cliente que o evento tinha adiantamento; estava em aberto.
    const adiantamentoRecebido = numero(adiantamento.recebido);
    const adiantamentoEmAberto = numero(adiantamento.em_aberto);

    const resumoNd = faixaResumo(resumo, 'nd');
    const resumoNf = faixaResumo(resumo, 'nf');
    const resumoNaoInformado = faixaResumo(resumo, 'nao_informado');
    const totalGeral = numero(resumo.total_geral);
    const semOpf = resumo.sem_opf ? numero(resumo.sem_opf.total) : 0;
    // Item 3 (12/10): Imposto NF Access — marcado no Efetivo entra no total da NF; desmarcado só informa.
    const nfAccess = resumo.imposto_nf_access || null;
    const rotuloSemOpf = (resumo.sem_opf && resumo.sem_opf.rotulo) || 'FEE / Agência';

    // Pendência impeditiva NÃO desabilita o botão (decisão do Rafael em
    // 27/08/2026: o sistema está estreando em produção e travar impediria
    // faturar evento que hoje já é faturado à mão). O que falta aparece na
    // região de Pendências e é repetido no modal de confirmação.
    //
    // O único disable que sobra é o de valor zero, e esse é real: o backend
    // recusa documento sem valor com 422.
    const totalDoTipo = function (tipo) {
        return tipo === 'nd' ? resumoNd.total : resumoNf.total;
    };
    const podeGerar = function (tipo) {
        if (!dados || !dados.evento || !dados.evento.id) return false;
        return totalDoTipo(tipo) > 0;
    };
    const motivoGerar = function (tipo) {
        if (!dados) return 'Carregando…';
        if (totalDoTipo(tipo) <= 0) return 'Nenhum fornecedor deste evento está marcado como ' + ROTULO_TIPO[tipo] + '.';
        if (impeditivas.length > 0) {
            return 'Gerar ' + ROTULO_TIPO[tipo] + ' de ' + reais(totalDoTipo(tipo))
                + ' — atenção: ainda há pendências neste evento.';
        }
        return 'Gerar ' + ROTULO_TIPO[tipo] + ' de ' + reais(totalDoTipo(tipo));
    };

    const confirmarGeracao = () => {
        if (!dados || !tipoGerar) { return; }
        const tipo = tipoGerar;
        setGerando(true);
        api({
            method: 'post',
            action: '/evento/' + dados.evento.id + '/documento',
            token: token,
            data: {
                tipo: tipo,
                valor: totalDoTipo(tipo),
                situacao: 'previsto',
                orcamento: dados.orcamento.id,
            },
        })
            .then(function () {
                toast.success('Documento gerado como previsto.');
                setTipoGerar(null);
                buscar();
            })
            .catch(function (err) {
                // Pendência não devolve mais 422. O que sobra aqui são as
                // recusas de verdade do store(): valor inválido, EMITIDO sem
                // número, documento duplicado no evento.
                const d = (err && err.response && err.response.data) || {};
                setTipoGerar(null);
                toast.error(d.error || d.mensagem || 'Erro ao gerar o documento.');
            })
            .finally(function () {
                setGerando(false);
            });
    };

    // Abre já com o que o documento tem hoje: a serie precisa vir preenchida
    // porque o update() troca `serie` por null quando recebe vazio — deixar o
    // campo em branco apagaria uma serie existente sem ninguem perceber.
    const abrirEmissao = function (d) {
        setDocEmitir(d);
        setNumeroDoc(d.numero ? String(d.numero) : '');
        setSerieDoc(d.serie ? String(d.serie) : '');
    };

    const fecharEmissao = function () {
        setDocEmitir(null);
        setNumeroDoc('');
        setSerieDoc('');
    };

    const confirmarEmissao = () => {
        if (!docEmitir || numeroDoc.trim() === '') { return; }
        setEmitindo(true);
        api({
            method: 'put',
            action: '/eventoDocumento/' + docEmitir.id,
            token: token,
            data: {
                numero: numeroDoc.trim(),
                serie: serieDoc.trim(),
                situacao: 'emitido',
            },
        })
            .then(function () {
                toast.success('Documento emitido.');
                fecharEmissao();
                buscar();
            })
            .catch(function (err) {
                // O backend recusa com 422 e uma mensagem que EXPLICA o caso
                // (número vazio, número já usado neste evento). Mostrar a dela:
                // "já existe ND 10 neste evento" diz o que fazer; "erro ao
                // emitir" não diz nada.
                const d = (err && err.response && err.response.data) || {};
                toast.error(d.error || d.mensagem || 'Erro ao emitir o documento.');
            })
            .finally(function () {
                setEmitindo(false);
            });
    };

    // ── PDF único da ND ───────────────────────────────────────────────────
    // A carta (que hoje a Simone monta na planilha Excel) MAIS os recibos e
    // bilhetes anexados, num arquivo só. É o passo que ela faz hoje no
    // IlovePDF: baixar tudo, subir tudo, juntar, mandar ao cliente.
    //
    // Regerar SUBSTITUI o PDF anterior (o backend apaga o antigo), então não
    // há confirmação: o botão vira "Gerar novamente" e o link de download
    // passa a apontar para o arquivo novo depois do `buscar()`.
    const gerarPdfNd = function (d) {
        setPdfGerando(d.id);
        api({
            method: 'post',
            action: '/eventoDocumento/' + d.id + '/gerarPdfCompleto',
            token: token,
        })
            .then(function (response) {
                const corpo = (response && response.data) || {};
                const conteudo = corpo.data || {};
                const faltando = conteudo.faltando || [];
                const ignorados = conteudo.ignorados || [];
                const paginas = parseInt(conteudo.paginas, 10) || 0;

                // O número de páginas é a confirmação de que os anexos entraram
                // — sem ele "PDF gerado" não distingue a carta sozinha do
                // documento completo.
                toast.success('PDF da ' + rotuloDocumento(d) + ' gerado — '
                    + plural(paginas, 'página', 'páginas') + '.');

                // Campo em branco e anexo de fora NÃO impedem: o PDF sai e está
                // lá. O aviso é o que ela precisa conferir antes de mandar ao
                // cliente — inclusive o que ainda terá de juntar à mão.
                if (faltando.length > 0 || ignorados.length > 0) {
                    setPdfFaltando({
                        rotulo: rotuloDocumento(d),
                        itens: faltando,
                        ignorados: ignorados,
                        paginas: paginas,
                    });
                }
                buscar();
            })
            .catch(function (err) {
                // 422 quando o documento não é ND (a NF vem da prefeitura) —
                // mostrar a mensagem do backend, que explica o caso.
                const c = (err && err.response && err.response.data) || {};
                toast.error(c.error || c.mensagem || 'Erro ao gerar o PDF da ND.');
            })
            .finally(function () {
                setPdfGerando(null);
            });
    };

    // Único ponto de download da tela. `/arquivo/download/{id}` é rota PÚBLICA
    // (fora do `auth:api`, api.php:212), então `window.open` resolve — não
    // precisa de fetch com header nem de blob. Serve para o PDF da ND, para os
    // anexos e para a nota fiscal do fornecedor: todos são uma linha em
    // `arquivo` com o mesmo id.
    const baixarArquivo = function (arquivoId, oQue) {
        if (!arquivoId) {
            toast.error('Este ' + (oQue || 'registro') + ' não tem arquivo para baixar.');
            return;
        }
        window.open(process.env.REACT_APP_API_URL + '/arquivo/download/' + arquivoId, '_blank');
    };

    // ── Excluir documento do evento ───────────────────────────────────────
    // O `destroy()` do backend é SOFT DELETE: carimba `removido_em`/`removido_por`
    // e o documento some do `show()` (que filtra por `vigentes()`); a linha
    // continua no banco. Recebimento vinculado NÃO é apagado — o backend só tira
    // o vínculo (`evento_documento_id` = null), porque o dinheiro entrou de
    // verdade: some do documento, não do evento. Daí o aviso no modal quando há
    // valor recebido, e o toast dizendo quantos ficaram órfãos.
    const confirmarExclusao = () => {
        if (!docExcluir) { return; }
        setExcluindo(true);
        api({
            method: 'delete',
            action: '/eventoDocumento/' + docExcluir.id,
            token: token,
        })
            .then(function (response) {
                const corpo = (response && response.data) || {};
                const conteudo = corpo.data || {};
                const orfaos = parseInt(conteudo.recebimentos_desvinculados, 10) || 0;
                toast.success(orfaos > 0
                    ? ('Documento excluído. '
                        + plural(orfaos, 'recebimento ficou', 'recebimentos ficaram')
                        + ' sem documento — revincule no Contas a Receber.')
                    : 'Documento excluído. Pode gerar de novo.');
                setDocExcluir(null);
                buscar();
            })
            .catch(function (err) {
                // 404 quando alguém já removeu em outra aba — a mensagem do
                // backend explica o caso melhor que um genérico.
                const d = (err && err.response && err.response.data) || {};
                toast.error(d.error || d.mensagem || 'Erro ao excluir o documento.');
            })
            .finally(function () {
                setExcluindo(false);
            });
    };

    // ── Planilha do orçamento ─────────────────────────────────────────────
    // É contra o resumo desse Excel que a conferência é feita — sem o link ela
    // teria de sair da tela, achar o orçamento no painel e baixar de lá.
    //
    // O endpoint é o mesmo do PainelOrcamento, mas o PAYLOAD não é o de lá:
    // `OrcamentoController::gerarPlanilhaExcel` faz `json_decode($request->data)`,
    // ou seja, espera UM campo `data` com o JSON `{token, rows}` — o formato do
    // `FormButton` da casa. (Conferido por curl em 03/09/2026: com `token` e
    // `rows[]` soltos, como o PainelOrcamento manda hoje, o endpoint devolve 500
    // "Trying to get property 'token' of non-object".)
    //
    // Form POST com `target=_blank` em vez de fetch+blob: o browser cuida do
    // download e o nome do arquivo vem do content-disposition do backend.
    const baixarPlanilhaOrcamento = function () {
        const form = document.createElement('form');
        form.method = 'post';
        form.action = process.env.REACT_APP_API_URL + '/orcamento/gerarPlanilhaExcel';
        form.target = '_blank';
        const campo = document.createElement('input');
        campo.type = 'hidden';
        campo.name = 'data';
        campo.value = JSON.stringify({ token: token, rows: [String(orcamentoId)] });
        form.appendChild(campo);
        document.body.appendChild(form);
        form.submit();
        document.body.removeChild(form);
    };

    // ── Anexos da ND ──────────────────────────────────────────────────────
    const abrirAnexo = function () {
        setAnexoAberto(true);
        setAnexoTipo('recibo');
        setAnexoDescricao('');
        setAnexoFile(null);
    };

    const fecharAnexo = function () {
        setAnexoAberto(false);
        setAnexoFile(null);
        setAnexoDescricao('');
    };

    // Mesma conta que o backend recusa com 422, com a mesma folga: sem arquivo
    // não envia, e o teto é o `MAX_KB` do controller.
    const motivoAnexo = function () {
        if (!anexoFile) return 'Escolha o arquivo para anexar.';
        if (anexoFile.size > MAX_ANEXO_BYTES) return 'Arquivo maior que 10 MB. Reduza o tamanho e tente de novo.';
        return null;
    };
    const impedimentoAnexo = motivoAnexo();

    const enviarAnexo = () => {
        if (!dados || impedimentoAnexo) { return; }
        setEnviandoAnexo(true);
        const formData = new FormData();
        formData.append('tipo', anexoTipo);
        formData.append('descricao', anexoDescricao.trim());
        formData.append('file', anexoFile);
        api({
            method: 'post',
            action: '/evento/' + dados.evento.id + '/faturamentoArquivo',
            token: token,
            data: formData,
        })
            .then(function () {
                toast.success('Arquivo anexado.');
                fecharAnexo();
                buscar();
            })
            .catch(function (err) {
                // O 422 do backend explica o caso (tipo inválido, extensão,
                // tamanho) — mostrar a mensagem dele, não um genérico.
                const d = (err && err.response && err.response.data) || {};
                toast.error(d.error || d.mensagem || 'Erro ao anexar o arquivo.');
            })
            .finally(function () {
                setEnviandoAnexo(false);
            });
    };

    const removerAnexo = () => {
        if (!anexoRemover) { return; }
        setRemovendoAnexo(true);
        api({
            method: 'delete',
            action: '/eventoFaturamentoArquivo/' + anexoRemover.id,
            token: token,
        })
            .then(function () {
                toast.success('Arquivo removido da lista.');
                setAnexoRemover(null);
                buscar();
            })
            .catch(function (err) {
                const d = (err && err.response && err.response.data) || {};
                toast.error(d.error || d.mensagem || 'Erro ao remover o arquivo.');
            })
            .finally(function () {
                setRemovendoAnexo(false);
            });
    };


    const tituloTela = dados ? (dados.evento.nome || 'Evento sem nome') : 'Conferência do faturamento';
    const subtituloTela = dados
        ? [
            dados.evento.codigo ? '#' + dados.evento.codigo : '',
            dados.orcamento.titulo || 'Orçamento sem título',
            'enviado em ' + dataHora(dados.orcamento.enviado_em),
        ].filter(Boolean).join(' · ')
        : 'Resumo ND x NF, pendências e documentos do evento.';

    return (
        <ErpTemplate>
            <div className="main-content container-fluid fin-pp">

                {/* Header */}
                <div className="fin-pp__head">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                        <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                            onClick={function () { history.push('/financeiroFaturamento'); }}
                            title="Voltar para a fila do Faturamento">
                            <FaArrowLeft size={13} />
                        </button>
                        <div style={{ minWidth: 0 }}>
                            <h1 className="fin-pp__title">{tituloTela}</h1>
                            <p className="fin-pp__subtitle">{subtituloTela}</p>
                        </div>
                    </div>
                    <div className="fin-pp__actions">
                        <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                            onClick={buscar} disabled={carregando}>
                            <FaSync size={13} /> {carregando ? 'Atualizando…' : 'Atualizar'}
                        </button>
                        {/* O Excel do orçamento é o documento contra o qual esta
                            conferência é feita. Fica aqui para ela não ter de
                            voltar ao painel de orçamentos só para baixá-lo. */}
                        <button type="button" className="fin-pp__btn fin-pp__btn--sm"
                            onClick={baixarPlanilhaOrcamento}
                            title="Baixar o Excel deste orçamento — é o resumo conferido contra esta tela">
                            <FaFileExcel size={13} /> Planilha do orçamento
                        </button>
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm"
                            disabled={!podeGerar('nd')} title={motivoGerar('nd')}
                            onClick={function () { setTipoGerar('nd'); }}>
                            <FaFileInvoiceDollar size={13} /> Gerar ND
                        </button>
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--sm"
                            disabled={!podeGerar('nf')} title={motivoGerar('nf')}
                            onClick={function () { setTipoGerar('nf'); }}>
                            <FaFileInvoiceDollar size={13} /> Gerar NF
                        </button>
                    </div>
                </div>

                {carregando && !dados && (
                    <div className="fin-pp__card">
                        <div className="fin-pp__empty" style={{ padding: '48px 0' }}>Carregando…</div>
                    </div>
                )}

                {!carregando && erro && (
                    <div className="fin-pp__card">
                        <div className="fin-pp__empty" style={{ padding: '40px 16px' }}>
                            <FaExclamationTriangle size={26} style={{ color: 'var(--fin-wn)' }} />
                            <div style={{ marginTop: 8 }}>{erro}</div>
                        </div>
                    </div>
                )}

                {dados && (<>
                    {/* ── 1. Resumo ND x NF ────────────────────────────────────
                        `.fin-pp__kpis` não tem margem própria: sem o marginBottom
                        a primeira região cola no card do KPI. */}
                    <section className="fin-pp__kpis" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">ND</span>
                                <span className="fin-pp__kpi-ic"><FaFileInvoiceDollar size={16} /></span>
                            </div>
                            <div className="fin-pp__kpi-value">{reais(resumoNd.total)}</div>
                            <OrigemMoeda origem={resumo.nd && resumo.nd.origem_moeda} />
                            <div className="fin-pp__kpi-sub">{plural(resumoNd.fornecedores, 'fornecedor', 'fornecedores')}</div>
                        </div>

                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">NF</span>
                                <span className="fin-pp__kpi-ic"><FaFileInvoiceDollar size={16} /></span>
                            </div>
                            <div className="fin-pp__kpi-value">{reais(resumoNf.total)}</div>
                            <OrigemMoeda origem={resumo.nf && resumo.nf.origem_moeda} />
                            <div className="fin-pp__kpi-sub">
                                {plural(resumoNf.fornecedores, 'fornecedor', 'fornecedores')}
                                {nfAccess && nfAccess.incluido ? ' + ' + (nfAccess.rotulo || 'Imposto NF Access') + ' ' + reais(nfAccess.valor) : ''}
                            </div>
                        </div>

                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Não informado</span>
                                <span className="fin-pp__kpi-ic"><FaExclamationTriangle size={16} /></span>
                            </div>
                            <div className={'fin-pp__kpi-value' + (resumoNaoInformado.total > 0 ? ' is-warn' : '')}>
                                {reais(resumoNaoInformado.total)}
                            </div>
                            <OrigemMoeda origem={resumo.nao_informado && resumo.nao_informado.origem_moeda} />
                            <div className="fin-pp__kpi-sub">
                                {resumoNaoInformado.total > 0
                                    ? plural(resumoNaoInformado.fornecedores, 'fornecedor', 'fornecedores') + ' sem ND/NF no Efetivo'
                                    : 'Todos os fornecedores classificados'}
                            </div>
                        </div>

                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Total geral</span>
                                <span className="fin-pp__kpi-ic"><FaBalanceScale size={16} /></span>
                            </div>
                            <div className="fin-pp__kpi-value">{reais(totalGeral)}</div>
                            <OrigemMoeda origem={resumo.origem_moeda} />
                            <div className="fin-pp__kpi-sub">
                                Orçado vigente dos fornecedores do evento
                                {nfAccess && nfAccess.incluido ? ' + ' + (nfAccess.rotulo || 'Imposto NF Access') : ''}
                            </div>
                        </div>

                        {nfAccess && numero(nfAccess.valor) > 0 && (
                            <div className="fin-pp__kpi" data-faturamento-nf-access={nfAccess.incluido ? 'incluido' : 'fora'}>
                                <div className="fin-pp__kpi-top">
                                    <span className="fin-pp__kpi-label">{nfAccess.rotulo || 'Imposto NF Access'}</span>
                                    <span className="fin-pp__kpi-ic"><FaFileInvoiceDollar size={16} /></span>
                                </div>
                                <div className={'fin-pp__kpi-value' + (nfAccess.incluido ? '' : ' is-warn')}>{reais(numero(nfAccess.valor))}</div>
                                <div className="fin-pp__kpi-sub">
                                    {nfAccess.incluido ? 'Incluído na NF do cliente (marcado no Efetivo)' : 'Fora da NF: não marcado no Efetivo'}
                                </div>
                            </div>
                        )}

                        {semOpf > 0 && (
                            <div className="fin-pp__kpi">
                                <div className="fin-pp__kpi-top">
                                    <span className="fin-pp__kpi-label">{rotuloSemOpf}</span>
                                    <span className="fin-pp__kpi-ic"><FaFileAlt size={16} /></span>
                                </div>
                                <div className="fin-pp__kpi-value">{reais(semOpf)}</div>
                                <div className="fin-pp__kpi-sub">Não entra em ND nem em NF</div>
                            </div>
                        )}

                        {/* O número grande é o RECEBIDO, não o disponível: era o
                            disponível que induzia a escrever "tem adiantamento" no
                            faturamento sem o dinheiro ter caído. `composicao.recebido`
                            NÃO serve de sublinha — aquilo é recebimento de ND/NF. */}
                        <div className="fin-pp__kpi">
                            <div className="fin-pp__kpi-top">
                                <span className="fin-pp__kpi-label">Adiantamento recebido</span>
                                <span className="fin-pp__kpi-ic"><FaWallet size={16} /></span>
                            </div>
                            <div className={'fin-pp__kpi-value' + (adiantamentoEmAberto > 0 ? ' is-warn' : '')}>
                                {reais(adiantamentoRecebido)}
                            </div>
                            <div className="fin-pp__kpi-sub">
                                {'Disponível ' + reais(adiantamento.disponivel)}
                                {composicao ? ' · usado ' + reais(composicao.usado_fornecedores) : ''}
                            </div>
                            {adiantamentoEmAberto > 0 && (
                                <div className="fin-pp__kpi-sub" style={{ marginTop: 6 }}>
                                    <span className="fin-pp__minitag is-warn">
                                        Em aberto {reais(adiantamentoEmAberto)}
                                    </span>
                                    <span style={{ marginLeft: 6 }}>ainda não recebido — não cite no faturamento</span>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ── 2. Checklist (D-0056, Rafael 03/11) ─────────────────
                        A mesma lista que travou o envio no pré-envio: tudo verde = a
                        Simone só confere. Substitui as "Pendências" de 27/08. */}
                    <ChecklistFaturamento checklist={dados && dados.checklist} editavel={false} />

                    {/* ── 2a. Fotografia da aprovação (detalhe do item do checklist) */}
                    {fotografia && (
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaExclamationTriangle size={16} /></span>
                            <div style={{ minWidth: 0, flex: 1 }}>
                                <h2 className="fin-pp__cphead-title">Fotografia da aprovação</h2>
                                <p className="fin-pp__cphead-sub">
                                    O orçamento de agora comparado com o que o cliente aprovou. Os valores da tela são os de agora.
                                </p>
                            </div>
                        </div>

                        <div className="fin-pp__region-body">
                            {/* D-0056 (Rafael 29/10): fotografia só ALERTA. Mostra o que mudou
                                no orçamento depois que o cliente aprovou; os valores da tela
                                continuam os de agora. */}
                            {fotografia && (
                                <div className="fin-pp__card" style={{ padding: '16px 18px' }} data-faturamento-fotografia={fotografia.situacao}>
                                    {fotografia.situacao === 'sem_fotografia' && (
                                        <div className="fin-pp__notice" style={{ margin: 0 }}>
                                            <span className="fin-pp__pill" style={{ flex: 'none' }}>Sem fotografia</span>
                                            <span style={{ flex: 1, minWidth: 0 }}>
                                                Este orçamento não tem a fotografia do Aprovar Cliente (aprovado antes de 12/10).
                                                Não há como comparar com o que o cliente aprovou.
                                            </span>
                                        </div>
                                    )}
                                    {/* Rafael 29/10: foto de antes da regra de 20/10 (grupos da Agência fora
                                        dos fornecedores) traz a Access com outra régua — não compara. */}
                                    {fotografia.situacao === 'regra_antiga' && (
                                        <div className="fin-pp__notice" style={{ margin: 0 }}>
                                            <span className="fin-pp__pill" style={{ flex: 'none' }}>Sem comparação</span>
                                            <span style={{ flex: 1, minWidth: 0 }}>
                                                A fotografia do Aprovar Cliente
                                                {fotografia.gravada_em ? ' (' + dataBr(fotografia.gravada_em) + ')' : ''} é
                                                anterior à regra de 20/10, que tirou os grupos da Agência dos fornecedores.
                                                Ela foi calculada de outro jeito e não serve para comparar com o orçamento de agora.
                                            </span>
                                        </div>
                                    )}
                                    {fotografia.situacao === 'igual' && (
                                        <div className="fin-pp__notice" style={{ margin: 0 }}>
                                            <span className="fin-pp__pill is-ok" style={{ flex: 'none' }}>Igual ao aprovado</span>
                                            <span style={{ flex: 1, minWidth: 0 }}>
                                                Nada mudou desde a aprovação do cliente
                                                {fotografia.gravada_em ? ' (' + dataBr(fotografia.gravada_em) + ')' : ''}.
                                            </span>
                                        </div>
                                    )}
                                    {fotografiaMudou && (
                                        <>
                                            <div className="fin-pp__notice" style={{ margin: 0 }}>
                                                <span className="fin-pp__pill is-warn" style={{ flex: 'none' }}>Mudou desde a aprovação</span>
                                                <span style={{ flex: 1, minWidth: 0 }}>
                                                    O orçamento mudou depois que o cliente aprovou
                                                    {fotografia.gravada_em ? ' (' + dataBr(fotografia.gravada_em) + ')' : ''}:
                                                    aprovado {reais(numero(fotografia.total_fotografia))}, agora {reais(numero(fotografia.total_atual))}.
                                                    Os valores desta tela são os de agora; confira antes de faturar.
                                                </span>
                                            </div>
                                            {(fotografiaDiferencas.length > 0 || fotografia.imposto_nf_access) && (
                                                <div className="fin-pp__tblwrap" style={{ marginTop: 12 }}>
                                                    <table className="fin-pp__tbl">
                                                        <thead>
                                                            <tr>
                                                                <th>Fornecedor</th>
                                                                <th style={{ width: 110 }}>O que mudou</th>
                                                                <th style={{ width: 130 }}>Aprovado</th>
                                                                <th style={{ width: 130 }}>Agora</th>
                                                                <th style={{ width: 130 }}>Diferença</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {fotografiaDiferencas.map(function (d) {
                                                                var rotulo = d.tipo === 'novo' ? 'Entrou' : (d.tipo === 'removido' ? 'Saiu' : 'Valor');
                                                                return (
                                                                    <tr key={'foto-' + d.fornecedor_id}>
                                                                        {/* Nome do cadastro vem sem espaço (ex.: SEGURADORA_ASISTBRAS_...): sem quebra invadia a coluna ao lado. */}
                                                                        <td className="fin-pp__cell-strong" style={{ whiteSpace: 'normal', wordBreak: 'break-word' }}>{d.fornecedor_nome || 'Fornecedor ' + d.fornecedor_id}</td>
                                                                        <td><span className="fin-pp__minitag is-warn">{rotulo}</span></td>
                                                                        <td className="fin-pp__cell-num">{reais(numero(d.valor_fotografia))}</td>
                                                                        <td className="fin-pp__cell-num">{reais(numero(d.valor_atual))}</td>
                                                                        <td className="fin-pp__cell-num">{reais(numero(d.diferenca))}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                            {fotografia.imposto_nf_access && (
                                                                <tr key="foto-nf-access">
                                                                    <td className="fin-pp__cell-strong">Imposto NF Access</td>
                                                                    <td><span className="fin-pp__minitag is-warn">Valor</span></td>
                                                                    <td className="fin-pp__cell-num">{reais(numero(fotografia.imposto_nf_access.valor_fotografia))}</td>
                                                                    <td className="fin-pp__cell-num">{reais(numero(fotografia.imposto_nf_access.valor_atual))}</td>
                                                                    <td className="fin-pp__cell-num">{reais(numero(fotografia.imposto_nf_access.diferenca))}</td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    </section>
                    )}

                    {/* ── 2b. Dados do evento (recolhível) ──────────────────────
                        Vem do MESMO payload que a tela do operador usa. Está aqui
                        para a Simone não precisar abrir evento por evento: pedido
                        do Rafael em 03/09 — "quero que ela resolva toda a vida dela
                        numa tela só". */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__grp">
                            <BlocoHead
                                aberto={blocosAbertos.evento}
                                onToggle={function () { alternarBloco('evento'); }}
                                icone={<FaCalendarAlt size={15} />}
                                titulo="Dados do evento"
                                subtitulo="Endereço, datas e responsáveis — o que identifica o evento na nota"
                                total={inicioEvento === '' ? null : inicioEvento}
                            />
                            {blocosAbertos.evento && (
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl fin-pp__tbl--kv">
                                        <tbody>
                                            <Linha label="Endereço">
                                                {evento.endereco ? evento.endereco : vazio}
                                                {evento.enderecocep ? (
                                                    <div className="fin-pp__cell-muted" style={{ marginTop: 4 }}>
                                                        CEP {evento.enderecocep}
                                                    </div>
                                                ) : null}
                                            </Linha>
                                            <Linha label="Local">
                                                {evento.localNaoDefinido
                                                    ? <span className="fin-pp__minitag is-warn">Local ainda não definido</span>
                                                    : (evento.local ? evento.local : vazio)}
                                            </Linha>
                                            <Linha label="Início">
                                                {inicioEvento === '' ? vazio : inicioEvento}
                                                {replanejadoInicio !== '' ? (
                                                    <div className="fin-pp__cell-muted" style={{ marginTop: 4 }}>
                                                        replanejado para {replanejadoInicio}
                                                    </div>
                                                ) : null}
                                            </Linha>
                                            <Linha label="Fim">
                                                {fimEvento === '' ? vazio : fimEvento}
                                                {replanejadoFim !== '' ? (
                                                    <div className="fin-pp__cell-muted" style={{ marginTop: 4 }}>
                                                        replanejado para {replanejadoFim}
                                                    </div>
                                                ) : null}
                                            </Linha>
                                            <Linha label="Fechamento">
                                                {fechamentoEvento === '' ? vazio : fechamentoEvento}
                                            </Linha>
                                            <Linha label="Previsão de faturamento">
                                                {previsaoFaturamento === '' ? vazio : previsaoFaturamento}
                                            </Linha>
                                            <Linha label="Responsável Access">
                                                {responsaveisAccess === '' ? vazio : responsaveisAccess}
                                            </Linha>
                                            <Linha label="Responsável do cliente">
                                                {respCliente === '' ? vazio : respCliente}
                                            </Linha>
                                            <Linha label="Diretor responsável">
                                                {diretorResp === '' ? vazio : diretorResp}
                                            </Linha>
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ── 2c. PO (recolhível) ───────────────────────────────────── */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__grp">
                            <BlocoHead
                                aberto={blocosAbertos.po}
                                onToggle={function () { alternarBloco('po'); }}
                                icone={<FaFileAlt size={15} />}
                                titulo="PO"
                                subtitulo="O número do pedido de compra vai impresso na ND"
                                total={po.nao_aplicavel
                                    ? 'não aplicável'
                                    : (po.numero ? String(po.numero) : 'sem número')}
                            />
                            {blocosAbertos.po && (
                                <div className="fin-pp__region-body">
                                    {po.nao_aplicavel ? (
                                        <div className="fin-pp__notice">
                                            <span className="fin-pp__pill is-info" style={{ flex: 'none' }}>Sem PO</span>
                                            <span style={{ flex: 1, minWidth: 0 }}>
                                                <strong>Este evento não tem PO.</strong> O cadastro está marcado
                                                como não aplicável — a ND sai com "PO não aplicável".
                                            </span>
                                        </div>
                                    ) : (!po.numero ? (
                                        <div className="fin-pp__notice">
                                            <span className="fin-pp__pill is-warn" style={{ flex: 'none' }}>Falta</span>
                                            <span style={{ flex: 1, minWidth: 0 }}>
                                                O evento não está marcado como "sem PO", mas o número não foi
                                                informado. A ND sai sem ele — corrija no cadastro do evento.
                                            </span>
                                        </div>
                                    ) : (
                                        <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                                            <div className="fin-pp__tblwrap">
                                                <table className="fin-pp__tbl fin-pp__tbl--kv">
                                                    <tbody>
                                                        <Linha label="Número">{po.numero}</Linha>
                                                        <Linha label="Arquivo">
                                                            {po.arquivo ? (
                                                                <button type="button"
                                                                    className="fin-pp__minitag is-info"
                                                                    onClick={function () { baixarArquivo(po.arquivo, 'a PO'); }}>
                                                                    <FaDownload size={11} /> Abrir a PO
                                                                </button>
                                                            ) : vazio}
                                                        </Linha>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ── 2d. Dados de cobrança (recolhível) ─────────────────────
                        É o rodapé bancário e o vencimento que a ND imprime. Ela
                        confere aqui antes de gerar. */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__grp">
                            <BlocoHead
                                aberto={blocosAbertos.cobranca}
                                onToggle={function () { alternarBloco('cobranca'); }}
                                icone={<FaUniversity size={15} />}
                                titulo="Dados de cobrança"
                                subtitulo="Banco, prazo e vencimento — sai impresso na ND"
                                total={contaCobranca ? contaCobranca.nome : 'não configurado'}
                            />
                            {blocosAbertos.cobranca && (
                                <div className="fin-pp__region-body">
                                    {!contaCobranca && (
                                        <div className="fin-pp__notice" style={{ marginBottom: 12 }}>
                                            <span className="fin-pp__pill is-warn" style={{ flex: 'none' }}>Falta</span>
                                            <span style={{ flex: 1, minWidth: 0 }}>
                                                Este cliente não tem conta de recebimento configurada. A ND vai
                                                sair <strong>sem o rodapé bancário</strong> — configure no cadastro
                                                do cliente antes de enviar.
                                            </span>
                                        </div>
                                    )}
                                    <div className="fin-pp__card" style={{ overflow: 'hidden' }}>
                                        <div className="fin-pp__tblwrap">
                                            <table className="fin-pp__tbl fin-pp__tbl--kv">
                                                <tbody>
                                                    <Linha label="Banco">
                                                        {contaCobranca ? contaCobranca.nome : vazio}
                                                    </Linha>
                                                    <Linha label="Agência">
                                                        {contaCobranca && contaCobranca.agencia ? contaCobranca.agencia : vazio}
                                                    </Linha>
                                                    <Linha label="Conta">
                                                        {contaCobranca && contaCobranca.conta ? contaCobranca.conta : vazio}
                                                    </Linha>
                                                    <Linha label="Prazo de pagamento">
                                                        {cobranca.prazo_dias
                                                            ? plural(cobranca.prazo_dias, 'dia', 'dias')
                                                            : vazio}
                                                    </Linha>
                                                    <Linha label="Dia de corte">
                                                        {cobranca.dia_corte ? cobranca.dia_corte : vazio}
                                                    </Linha>
                                                    <Linha label="Vencimento sugerido">
                                                        {cobranca.vencimento_sugerido
                                                            ? dataBr(cobranca.vencimento_sugerido)
                                                            : vazio}
                                                    </Linha>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ── 2e. Destinatários (recolhível, editável) ───────────────
                        Editável porque é ela quem envia: se o operador esqueceu ou
                        errou, corrige aqui sem trocar de tela. */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__grp">
                            <BlocoHead
                                aberto={blocosAbertos.destinatarios}
                                onToggle={function () { alternarBloco('destinatarios'); }}
                                icone={<FaEnvelope size={15} />}
                                titulo="Destinatários do faturamento"
                                subtitulo="Para quem o faturamento vai ser enviado"
                                total={listaEmails.length > 0
                                    ? plural(listaEmails.length, 'endereço', 'endereços')
                                    : 'nenhum'}
                            />
                            {blocosAbertos.destinatarios && (
                                <div className="fin-pp__region-body">
                                    <div className="fin-pp__card" style={{ padding: '16px 18px' }}>
                                        <div className="fin-pp__filters">
                                            <FinField label="E-mails" htmlFor="emailsFaturamentoSimone" full>
                                                <FinInput
                                                    id="emailsFaturamentoSimone"
                                                    type="text"
                                                    placeholder="fulano@cliente.com, financeiro@cliente.com"
                                                    value={emails}
                                                    onChange={function (e) { setEmails(e.target.value); }}
                                                />
                                            </FinField>
                                        </div>
                                        {emailsRuins.length > 0 && (
                                            <div className="fin-pp__notice" style={{ margin: '12px 0 0' }}>
                                                <span className="fin-pp__pill is-danger" style={{ flex: 'none' }}>Corrija</span>
                                                <span style={{ flex: 1, minWidth: 0 }}>
                                                    {emailsRuins.length === 1
                                                        ? 'E-mail inválido: ' + emailsRuins[0] + '.'
                                                        : 'E-mails inválidos: ' + emailsRuins.join(', ') + '.'}
                                                </span>
                                            </div>
                                        )}
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
                                            <button type="button"
                                                className="fin-pp__btn fin-pp__btn--sm"
                                                disabled={salvandoEmails || emailsRuins.length > 0 || !emailsAlterados}
                                                onClick={salvarEmails}>
                                                {salvandoEmails ? 'Salvando…' : 'Salvar e-mails'}
                                            </button>
                                            {emailsAlterados && emailsRuins.length === 0 && (
                                                <span className="fin-pp__minitag is-warn">Não salvo</span>
                                            )}
                                            {!emailsAlterados && listaEmails.length > 0 && (
                                                <span className="fin-pp__minitag is-ok">Salvo</span>
                                            )}
                                        </div>
                                        <p className="fin-pp__cell-muted" style={{ margin: '10px 0 0' }}>
                                            Separe por vírgula para informar mais de um. Salvar aqui só guarda o
                                            destinatário — não reenvia o orçamento para a fila.
                                        </p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ── 3. Fornecedores (recolhível + filtro por nome) ────────
                        A lista cresce com o evento e empurrava o resto da tela
                        para baixo. Recolhida, o cabeçalho ainda entrega o que ela
                        cruza com a planilha: quantos fornecedores e o total. */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__grp">
                            <BlocoHead
                                aberto={blocosAbertos.fornecedores}
                                onToggle={function () { alternarBloco('fornecedores'); }}
                                icone={<FaTruck size={15} />}
                                titulo="Fornecedores"
                                subtitulo="ND/NF vem do Efetivo. Orçado é o valor vigente (Fechamento → Negociado → Pré-Evento)."
                                badge={
                                    <span className="fin-pp__grp-badge">
                                        {plural(fornecedores.length, 'fornecedor', 'fornecedores')}
                                    </span>
                                }
                                total={<>Total: <strong>{reais(totalGeral)}</strong></>}
                            />

                            {blocosAbertos.fornecedores && (<>
                                {/* Filtro por nome. Fica DENTRO do bloco: só faz
                                    sentido com a lista à vista, e recolhido não
                                    ocupa linha nenhuma. */}
                                <div style={{ padding: '12px 14px 0' }}>
                                    <div className="fin-pp__search-field" style={{ maxWidth: 420 }}>
                                        <FaSearch className="fin-pp__search-ic" size={13} />
                                        <input type="text" className="fin-pp__input"
                                            value={filtroFornecedor}
                                            placeholder="Filtrar por fornecedor…"
                                            onChange={function (e) { setFiltroFornecedor(e.target.value); }} />
                                    </div>
                                    {termoFornecedor !== '' && (
                                        <div className="fin-pp__cell-muted" style={{ margin: '8px 0 0' }}>
                                            Mostrando {fornecedoresFiltrados.length} de {fornecedores.length} —
                                            o total do cabeçalho continua sendo o do evento inteiro.
                                        </div>
                                    )}
                                </div>

                                <div className="fin-pp__tblwrap" style={{ marginTop: 12 }}>
                                    <table className="fin-pp__tbl fin-pp__tbl--wide">
                                        <thead>
                                            <tr>
                                                <th>Fornecedor</th>
                                                <th style={{ width: 170 }}>Tipo</th>
                                                <th style={{ width: 150 }}>Orçado</th>
                                                <th style={{ width: 170 }}>Pago</th>
                                                <th style={{ width: 170 }}>Diferença</th>
                                                <th style={{ width: 300 }}>Notas fiscais</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fornecedoresFiltrados.length === 0 && (
                                                <tr>
                                                    <td colSpan={6} className="fin-pp__empty">
                                                        {termoFornecedor !== ''
                                                            ? 'Nenhum fornecedor com “' + filtroFornecedor.trim() + '” no nome.'
                                                            : 'Nenhum fornecedor neste orçamento.'}
                                                    </td>
                                                </tr>
                                            )}
                                            {fornecedoresFiltrados.map(function (f) {
                                                var diferenca = numero(f.divergencia);
                                                var temDiferenca = Math.abs(diferenca) >= 0.01;
                                                var notas = f.nfs || [];
                                                var outroOrcamento = f.orcamento_origem
                                                    && String(f.orcamento_origem) !== String(dados.orcamento.id);
                                                return (
                                                    <tr key={f.fornecedor_id}>
                                                        <td className="fin-pp__cell-strong">{f.fornecedor_nome || '—'}</td>
                                                        <td>
                                                            <span className={'fin-pp__minitag ' + (f.tipo ? 'is-info' : 'is-warn')}>
                                                                {rotuloTipo(f.tipo)}
                                                            </span>
                                                            {outroOrcamento && (
                                                                <div className="fin-pp__cell-muted" style={{ marginTop: 4 }}>
                                                                    definido em outro orçamento do evento
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="fin-pp__cell-num">
                                                            {reais(f.valor_orcado)}
                                                            <OrigemMoeda origem={f.origem_moeda} />
                                                        </td>
                                                        <td className="fin-pp__cell-num">
                                                            {reais(f.valor_pago)}
                                                            {!f.pagamento_realizado && (
                                                                <div style={{ marginTop: 4 }}>
                                                                    <span className="fin-pp__minitag is-warn">sem pagamento</span>
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="fin-pp__cell-num">
                                                            {!temDiferenca ? (
                                                                <span className="fin-pp__cell-muted">—</span>
                                                            ) : (
                                                                <>
                                                                    <span style={{ color: 'var(--fin-wn)' }}>
                                                                        {reais(Math.abs(diferenca))}
                                                                    </span>
                                                                    <div className="fin-pp__cell-muted" style={{ marginTop: 4 }}>
                                                                        {diferenca > 0 ? 'pago a menos' : 'pago a mais'}
                                                                    </div>
                                                                </>
                                                            )}
                                                        </td>
                                                        <td>
                                                            {notas.length === 0 ? (
                                                                <span className="fin-pp__cell-muted">
                                                                    {f.tipo === 'nf' ? 'Nenhuma nota anexada' : '—'}
                                                                </span>
                                                            ) : (
                                                                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                                                                    {notas.map(function (n) {
                                                                        var rotuloNf = 'NF ' + (n.numero || 's/nº')
                                                                            + (n.status ? ' · ' + n.status : '');
                                                                        // Sem `arquivo_id` não há o que abrir — fica
                                                                        // tag neutra em vez de botão morto. Com
                                                                        // arquivo vira azul e ganha o ícone: é o
                                                                        // sinal de que dá para clicar.
                                                                        if (!n.arquivo_id) {
                                                                            return (
                                                                                <span key={n.id} className="fin-pp__minitag"
                                                                                    title="Esta nota não tem arquivo anexado no Efetivo">
                                                                                    {rotuloNf}
                                                                                </span>
                                                                            );
                                                                        }
                                                                        return (
                                                                            <button key={n.id} type="button"
                                                                                className="fin-pp__minitag is-info"
                                                                                style={{ cursor: 'pointer', fontFamily: 'inherit', gap: 5 }}
                                                                                onClick={function () { baixarArquivo(n.arquivo_id, 'nota fiscal'); }}
                                                                                title={'Abrir ' + (n.nome || 'a nota fiscal deste fornecedor')}>
                                                                                <FaDownload size={9} /> {rotuloNf}
                                                                            </button>
                                                                        );
                                                                    })}
                                                                </div>
                                                            )}
                                                            {numero(f.nf_valor_liquido) > 0 && (
                                                                <div className="fin-pp__cell-muted" style={{ marginTop: 6 }}>
                                                                    líquido {reais(f.nf_valor_liquido)}
                                                                </div>
                                                            )}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>)}
                        </div>
                    </section>

                    {/* ── 4. Documentos do evento (recolhível) ─────────────────
                        Fica ABERTO por padrão: é a tabela mais curta e é onde
                        moram as ações (informar número, gerar PDF, excluir).
                        Esconder a área de ação custaria um clique toda visita
                        sem encurtar a tela de forma perceptível. */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__grp">
                            <BlocoHead
                                aberto={blocosAbertos.documentos}
                                onToggle={function () { alternarBloco('documentos'); }}
                                icone={<FaFileAlt size={15} />}
                                titulo="Documentos do evento"
                                subtitulo="O documento nasce como previsto; o número entra depois de emitir na prefeitura."
                                badge={
                                    <span className="fin-pp__grp-badge">
                                        {plural(documentos.length, 'documento', 'documentos')}
                                    </span>
                                }
                                total={documentos.length === 0 ? null : (
                                    <>{documentosEmitidos} emitido{documentosEmitidos === 1 ? '' : 's'}
                                        {documentosPrevistos > 0
                                            ? ' · ' + documentosPrevistos + ' previsto' + (documentosPrevistos === 1 ? '' : 's')
                                            : ''}</>
                                )}
                            />

                            {blocosAbertos.documentos && (
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <thead>
                                            <tr>
                                                <th>Documento</th>
                                                <th style={{ width: 160 }}>Número</th>
                                                <th style={{ width: 180 }}>Valor</th>
                                                <th style={{ width: 140 }}>Situação</th>
                                                <th style={{ width: 380 }}>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {documentos.length === 0 && (
                                                <tr>
                                                    <td colSpan={5} className="fin-pp__empty">
                                                        Nenhum documento gerado ainda.
                                                    </td>
                                                </tr>
                                            )}
                                            {documentos.map(function (d) {
                                                var num = numeroDocumento(d);
                                                var previsto = String(d.situacao || '').toLowerCase() === 'previsto';
                                                // Só a ND é gerada aqui — a NF vem da prefeitura, e
                                                // o backend recusa com 422 se tentar.
                                                var ehNd = String(d.tipo || '').toLowerCase() === 'nd';
                                                var temPdf = !!d.arquivo;
                                                var gerandoEste = pdfGerando === d.id;
                                                return (
                                                    <tr key={d.id}>
                                                        <td className="fin-pp__cell-strong">{rotuloDocumento(d)}</td>
                                                        <td>
                                                            {num !== '' ? (
                                                                <span className="fin-pp__cell-strong">{num}</span>
                                                            ) : (
                                                                <span className="fin-pp__cell-muted">ainda sem número</span>
                                                            )}
                                                        </td>
                                                        <td className="fin-pp__cell-num">{reais(d.valor)}</td>
                                                        <td>
                                                            <span className={'fin-pp__minitag' + toneSituacao(d.situacao)}>
                                                                {d.situacao || '—'}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                                {/* Só o previsto oferece emitir: o emitido já tem número
                                                                    e reemitir seria trocar o que o cliente recebeu. */}
                                                                {previsto && (
                                                                    <button type="button"
                                                                        className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--primary"
                                                                        onClick={function () { abrirEmissao(d); }}
                                                                        title="Informar o número emitido na prefeitura e passar para emitido">
                                                                        <FaHashtag size={12} /> Informar número
                                                                    </button>
                                                                )}

                                                                {/* A carta da ND (hoje uma planilha à mão) JUNTO com
                                                                    os recibos e bilhetes anexados, num PDF só — o
                                                                    que ela monta hoje no IlovePDF.

                                                                    O botão diz quantos anexos vão junto: sem isso
                                                                    ela clica sem saber se o documento sai completo,
                                                                    e é exatamente o que precisa conferir antes de
                                                                    mandar ao cliente.

                                                                    Regerar substitui o PDF anterior — não acumula
                                                                    arquivo nem link. */}
                                                                {ehNd && (
                                                                    <button type="button"
                                                                        className={'fin-pp__btn fin-pp__btn--sm' + (temPdf ? '' : ' fin-pp__btn--primary')}
                                                                        disabled={gerandoEste}
                                                                        onClick={function () { gerarPdfNd(d); }}
                                                                        title={anexos.length > 0
                                                                            ? ('Junta a carta da ND com '
                                                                                + plural(anexos.length, 'arquivo anexado', 'arquivos anexados')
                                                                                + ' num PDF só'
                                                                                + (temPdf ? ' (substitui o anterior)' : ''))
                                                                            : ('Gerar o PDF da Nota de Débito — este evento ainda não tem anexo'
                                                                                + (temPdf ? ' (substitui o anterior)' : ''))}>
                                                                        <FaFilePdf size={12} />{' '}
                                                                        {gerandoEste ? 'Gerando…' : (temPdf ? 'Gerar novamente' : 'Gerar PDF')}
                                                                        {!gerandoEste && anexos.length > 0
                                                                            ? ' (ND + ' + plural(anexos.length, 'anexo', 'anexos') + ')'
                                                                            : ''}
                                                                    </button>
                                                                )}

                                                                {ehNd && temPdf && (
                                                                    <button type="button"
                                                                        className="fin-pp__btn fin-pp__btn--sm"
                                                                        onClick={function () { baixarArquivo(d.arquivo, 'documento'); }}
                                                                        title="Baixar o PDF da Nota de Débito">
                                                                        <FaDownload size={12} /> Baixar
                                                                    </button>
                                                                )}

                                                                {/* Excluir vale para previsto E emitido: o pedido é
                                                                    poder apagar para gerar de novo, e é justamente o
                                                                    emitido com número errado que precisa sair. Quem
                                                                    segura a mão é o modal de confirmação. */}
                                                                <button type="button"
                                                                    className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                                    onClick={function () { setDocExcluir(d); }}
                                                                    title="Excluir este documento para poder gerar de novo">
                                                                    <FaTrash size={11} /> Excluir
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* ── 5. Documentos para a ND (recolhível) ─────────────────
                        Recibos e PDFs do aéreo que hoje a Simone junta à mão no
                        IlovePDF antes de mandar a ND ao cliente. Quem sobe é o
                        FINANCEIRO, aqui — não o operador de Evento.

                        A seção aparece SEMPRE, mesmo em evento sem aéreo:
                          - recibo não é assunto de aéreo, entra em qualquer ND;
                          - `eventoaereo` só tem linha quando o operador cadastrou
                            os trechos, e é justamente no evento com cadastro
                            incompleto que o Financeiro mais precisa anexar o
                            bilhete à mão. Esconder a área ali seria fechar a
                            porta no caso que ela existe para resolver.
                        O que muda com o aéreo é o AVISO: o pill diz quantos
                        trechos o evento tem, para ela saber que há bilhete a
                        procurar. Recolhida ≠ escondida: o cabeçalho continua
                        dizendo quantos arquivos há, quantos trechos aéreos o
                        evento tem e mantém o "Anexar arquivo" clicável. */}
                    <section className="fin-pp__region" style={{ marginBottom: 18 }}>
                        <div className="fin-pp__grp">
                            <BlocoHead
                                aberto={blocosAbertos.anexos}
                                onToggle={function () { alternarBloco('anexos'); }}
                                icone={<FaPaperclip size={15} />}
                                titulo="Documentos para a ND"
                                subtitulo="Recibos e comprovantes do aéreo que entram no PDF enviado ao cliente junto da ND. PDF de preferência; imagem também é aceita."
                                badge={<>
                                    {trechosAereo > 0 && (
                                        <span className="fin-pp__minitag is-info" style={{ gap: 5 }}>
                                            <FaPlane size={11} /> {plural(trechosAereo, 'trecho aéreo', 'trechos aéreos')}
                                        </span>
                                    )}
                                    <span className="fin-pp__grp-badge">
                                        {plural(anexos.length, 'arquivo', 'arquivos')}
                                    </span>
                                </>}
                                acoes={
                                    <button type="button"
                                        className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--primary"
                                        onClick={abrirAnexo}
                                        title="Anexar um recibo ou o PDF do aéreo deste evento">
                                        <FaUpload size={12} /> Anexar arquivo
                                    </button>
                                }
                            />

                            {blocosAbertos.anexos && (
                                <div className="fin-pp__tblwrap">
                                    <table className="fin-pp__tbl">
                                        <thead>
                                            <tr>
                                                <th style={{ width: 120 }}>Tipo</th>
                                                <th>Descrição</th>
                                                <th style={{ width: 280 }}>Arquivo</th>
                                                <th style={{ width: 220 }}>Enviado por</th>
                                                <th style={{ width: 120 }}>Ações</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {anexos.length === 0 && (
                                                <tr>
                                                    <td colSpan={5} className="fin-pp__empty">
                                                        <div style={{ maxWidth: 560, margin: '0 auto' }}>
                                                            <strong>Nenhum arquivo anexado ainda.</strong>
                                                            <div style={{ marginTop: 6, lineHeight: 1.55 }}>
                                                                Suba aqui os recibos e os comprovantes do aéreo deste evento.
                                                                Eles ficam guardados no evento e são o material que compõe o
                                                                PDF único da ND enviado ao cliente.
                                                                {trechosAereo > 0
                                                                    ? ' Este evento tem aéreo cadastrado — os bilhetes provavelmente fazem falta.'
                                                                    : ' Este evento não tem aéreo cadastrado, mas recibos entram do mesmo jeito.'}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                            {anexos.map(function (a) {
                                                return (
                                                    <tr key={a.id}>
                                                        <td>
                                                            <span className={'fin-pp__minitag ' + (a.tipo === 'aereo' ? 'is-info' : '')}>
                                                                {a.tipo_rotulo || a.tipo}
                                                            </span>
                                                        </td>
                                                        <td className="fin-pp__cell-strong">
                                                            {a.descricao || <span className="fin-pp__cell-muted">sem descrição</span>}
                                                        </td>
                                                        <td>
                                                            {/* Nome de arquivo é longo por natureza
                                                                ("bilhete-paulo-tonidandel-ida.pdf") —
                                                                sem o corte ele estoura a célula. */}
                                                            <button type="button"
                                                                className="fin-pp__btn fin-pp__btn--sm"
                                                                style={{ maxWidth: '100%' }}
                                                                onClick={function () { baixarArquivo(a.arquivo_id, 'anexo'); }}
                                                                title={'Baixar ' + (a.nome || 'arquivo')}>
                                                                <FaDownload size={11} style={{ flex: 'none' }} />
                                                                <span style={{
                                                                    overflow: 'hidden',
                                                                    textOverflow: 'ellipsis',
                                                                    whiteSpace: 'nowrap',
                                                                }}>{a.nome || 'arquivo'}</span>
                                                            </button>
                                                        </td>
                                                        <td className="fin-pp__cell-muted">
                                                            {a.enviado_por || '—'}
                                                            <div style={{ marginTop: 4 }}>{dataHora(a.created_at)}</div>
                                                        </td>
                                                        <td>
                                                            <button type="button"
                                                                className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--danger-ghost"
                                                                onClick={function () { setAnexoRemover(a); }}
                                                                title="Tirar este arquivo da lista da ND">
                                                                <FaTrash size={11} /> Remover
                                                            </button>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </section>
                </>)}

                {/* Modal — confirmar a geração do documento */}
                {dados && tipoGerar && (
                    <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={function (e) { if (e.target === e.currentTarget) { setTipoGerar(null); } }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic"><FaFileInvoiceDollar size={17} /></span>
                                    <h3 className="fin-pp__modal-title">Gerar {ROTULO_TIPO[tipoGerar]}
                                        <span className="fin-pp__modal-sub">{dados.evento.nome}</span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close"
                                        onClick={function () { setTipoGerar(null); }}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <p style={{ margin: '0 0 10px' }}>
                                        Valor: <strong>{reais(totalDoTipo(tipoGerar))}</strong>
                                    </p>

                                    {/* Pendência não impede mais gerar (27/08/2026), mas quem
                                        confirma tem de ver na hora o que está faltando. */}
                                    {impeditivas.length > 0 && (
                                        <div className="fin-pp__notice"
                                            style={{ margin: '0 0 12px', alignItems: 'flex-start' }}>
                                            <span className="fin-pp__pill is-danger" style={{ flex: 'none' }}>Atenção</span>
                                            <div style={{ flex: 1, minWidth: 0 }}>
                                                <strong>
                                                    {plural(
                                                        impeditivas.length,
                                                        'pendência ainda não resolvida',
                                                        'pendências ainda não resolvidas'
                                                    )} neste evento. O documento vai ser gerado mesmo assim.
                                                </strong>
                                                <ul style={{ margin: '6px 0 0 18px', padding: 0 }}>
                                                    {impeditivas.map(function (texto, i) {
                                                        return (
                                                            <li key={'confirma-' + i} style={{ marginBottom: 4 }}>{texto}</li>
                                                        );
                                                    })}
                                                </ul>
                                            </div>
                                        </div>
                                    )}

                                    <p className="fin-pp__cell-muted" style={{ margin: 0 }}>
                                        O documento nasce como <strong>previsto</strong>. O número você preenche
                                        depois de emitir na prefeitura.
                                    </p>
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn"
                                        onClick={function () { setTipoGerar(null); }}>Cancelar</button>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary"
                                        disabled={gerando} onClick={confirmarGeracao}>
                                        {gerando ? 'Gerando…' : (impeditivas.length > 0 ? 'Gerar mesmo assim' : 'Gerar')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal — informar o número e emitir (2o tempo do fluxo).
                    A numeração nasce FORA do Access: a Simone emite no sistema da
                    prefeitura e volta com o número. Por isso o documento fica
                    `previsto` até aqui — e é este passo que solta o evento da
                    faixa "Em andamento" na fila do Faturamento. */}
                {docEmitir && (
                    <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={function (e) { if (e.target === e.currentTarget) { fecharEmissao(); } }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic is-ok"><FaHashtag size={17} /></span>
                                    <h3 className="fin-pp__modal-title">Informar número e emitir
                                        <span className="fin-pp__modal-sub">{dados && dados.evento ? dados.evento.nome : ''}</span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close"
                                        onClick={fecharEmissao}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    {/* Rótulo e valor à vista: é o que ela confere contra
                                        o documento que acabou de emitir na prefeitura. */}
                                    <div className="fin-pp__notice" style={{ margin: '0 0 14px' }}>
                                        <span className="fin-pp__pill is-info" style={{ flex: 'none' }}>
                                            {rotuloDocumento(docEmitir)}
                                        </span>
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            Valor do documento: <strong>{reais(docEmitir.valor)}</strong>
                                        </span>
                                    </div>

                                    <div className="fin-pp__filters">
                                        <FinField label="Número" required htmlFor="doc-numero" grow>
                                            <FinInput id="doc-numero" value={numeroDoc} maxLength={60}
                                                placeholder="ex.: 10"
                                                onChange={function (e) { setNumeroDoc(e.target.value); }} />
                                        </FinField>
                                        <FinField label="Série" htmlFor="doc-serie">
                                            <FinInput id="doc-serie" value={serieDoc} maxLength={20}
                                                placeholder="opcional"
                                                onChange={function (e) { setSerieDoc(e.target.value); }} />
                                        </FinField>
                                    </div>

                                    <p className="fin-pp__cell-muted" style={{ margin: '12px 0 0' }}>
                                        Este é o número gerado no <strong>sistema da prefeitura</strong>, não um número
                                        do Access. Emita lá primeiro e traga o número de volta para cá.
                                    </p>

                                    {/* Aviso acima do botão + botão desabilitado: a mesma
                                        regra que o backend recusa com 422. */}
                                    {numeroDoc.trim() === '' && (
                                        <div className="fin-pp__notice" style={{ margin: '12px 0 0' }}>
                                            <span className="fin-pp__pill is-warn" style={{ flex: 'none' }}>Falta</span>
                                            <span style={{ flex: 1, minWidth: 0 }}>
                                                Sem o número não dá para emitir — é por ele que o cliente é cobrado.
                                            </span>
                                        </div>
                                    )}
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn"
                                        onClick={fecharEmissao} disabled={emitindo}>Cancelar</button>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary"
                                        disabled={emitindo || numeroDoc.trim() === ''}
                                        title={numeroDoc.trim() === '' ? 'Informe o número para emitir.' : 'Marcar como emitido'}
                                        onClick={confirmarEmissao}>
                                        {emitindo ? 'Emitindo…' : 'Emitir'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal — confirmar a exclusão do documento.
                    Soft delete no backend: o documento sai desta tela e do saldo
                    do evento, a linha continua no banco com `removido_em`. O que
                    NÃO volta é o vínculo dos recebimentos — por isso o valor
                    recebido aparece em destaque antes de confirmar. */}
                {docExcluir && (
                    <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={function (e) { if (e.target === e.currentTarget) { setDocExcluir(null); } }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic is-danger"><FaTrash size={17} /></span>
                                    <h3 className="fin-pp__modal-title">Excluir documento
                                        <span className="fin-pp__modal-sub">
                                            {dados && dados.evento ? dados.evento.nome : ''}
                                        </span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close"
                                        onClick={function () { setDocExcluir(null); }}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    {/* Rótulo e valor à vista: é o que ela confere
                                        antes de apagar — a tabela tem mais de uma
                                        linha e ND e NF do mesmo evento se parecem. */}
                                    <div className="fin-pp__notice" style={{ margin: '0 0 12px' }}>
                                        <span className="fin-pp__pill is-info" style={{ flex: 'none' }}>
                                            {docExcluir.rotulo || rotuloDocumento(docExcluir)}
                                        </span>
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            Valor do documento: <strong>{reais(docExcluir.valor)}</strong>
                                            {' · '}{docExcluir.situacao || '—'}
                                        </span>
                                    </div>

                                    {/* Aviso acima do botão: mesma conta do backend.
                                        O `destroy()` desvincula o recebimento em vez
                                        de apagar, e sem este aviso a Simone só
                                        descobriria no Contas a Receber. */}
                                    {numero(docExcluir.recebido) > 0 && (
                                        <div className="fin-pp__notice"
                                            style={{ margin: '0 0 12px', alignItems: 'flex-start' }}>
                                            <span className="fin-pp__pill is-danger" style={{ flex: 'none' }}>Atenção</span>
                                            <span style={{ flex: 1, minWidth: 0 }}>
                                                Este documento já tem <strong>{reais(docExcluir.recebido)}</strong> recebidos.
                                                O dinheiro continua no evento, mas o recebimento fica sem documento e
                                                terá de ser revinculado no Contas a Receber.
                                            </span>
                                        </div>
                                    )}

                                    <p style={{ margin: 0 }}>
                                        O documento sai desta tela e do saldo do evento. Depois de excluir você
                                        pode gerar {rotuloDocumento(docExcluir)} de novo, com o valor atual dos
                                        fornecedores.
                                    </p>
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn"
                                        onClick={function () { setDocExcluir(null); }}
                                        disabled={excluindo}>Cancelar</button>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--danger"
                                        disabled={excluindo} onClick={confirmarExclusao}>
                                        {excluindo ? 'Excluindo…' : 'Excluir'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal — anexar recibo / PDF do aéreo */}
                {dados && anexoAberto && (
                    <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={function (e) { if (e.target === e.currentTarget) { fecharAnexo(); } }}>
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic"><FaPaperclip size={17} /></span>
                                    <h3 className="fin-pp__modal-title">Anexar documento da ND
                                        <span className="fin-pp__modal-sub">{dados.evento.nome}</span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close" onClick={fecharAnexo}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <div className="fin-pp__filters">
                                        <FinField label="Tipo" required>
                                            <ReactSelect
                                                options={TIPOS_ANEXO}
                                                value={TIPOS_ANEXO.filter(function (t) { return t.value === anexoTipo; })}
                                                isClearable={false}
                                                onChange={function (e) { setAnexoTipo(e ? e.value : 'recibo'); }} />
                                        </FinField>
                                        <FinField label="Descrição" grow htmlFor="anexo-descricao"
                                            ajuda="Como esse arquivo vai ser reconhecido depois: 'Bilhete Paulo Tonidandel — ida', 'Recibo do transfer'.">
                                            <FinInput id="anexo-descricao" value={anexoDescricao} maxLength={255}
                                                placeholder="opcional"
                                                onChange={function (e) { setAnexoDescricao(e.target.value); }} />
                                        </FinField>
                                    </div>

                                    {/* Input de arquivo escondido atrás de um botão do
                                        design system — `form-control` cru não entra em
                                        tela redesenhada. */}
                                    <div className="fin-pp__field fin-pp__field--full" style={{ marginTop: 4 }}>
                                        <span className="fin-pp__label">Arquivo *</span>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                                            <label className="fin-pp__btn fin-pp__btn--sm"
                                                style={{ cursor: enviandoAnexo ? 'wait' : 'pointer', marginBottom: 0 }}>
                                                <FaUpload size={12} /> Escolher arquivo
                                                <input type="file" style={{ display: 'none' }}
                                                    accept={ACEITE_ANEXO} disabled={enviandoAnexo}
                                                    onChange={function (e) {
                                                        const f = e.target.files && e.target.files[0];
                                                        e.target.value = '';   // permite reescolher o mesmo arquivo
                                                        setAnexoFile(f || null);
                                                    }} />
                                            </label>
                                            {anexoFile ? (
                                                <span className="fin-pp__minitag is-ok">{anexoFile.name}</span>
                                            ) : (
                                                <span className="fin-pp__cell-muted">Nenhum arquivo escolhido</span>
                                            )}
                                        </div>
                                    </div>

                                    <p className="fin-pp__cell-muted" style={{ margin: '12px 0 0' }}>
                                        Aceita <strong>PDF</strong>, JPG e PNG, até 10 MB. O arquivo fica guardado
                                        no evento e vai compor o PDF único enviado ao cliente junto da ND.
                                    </p>

                                    {/* Aviso acima do botão + botão desabilitado: a mesma
                                        conta que o backend recusa com 422. */}
                                    {impedimentoAnexo && (
                                        <div className="fin-pp__notice" style={{ margin: '12px 0 0' }}>
                                            <span className="fin-pp__pill is-warn" style={{ flex: 'none' }}>Falta</span>
                                            <span style={{ flex: 1, minWidth: 0 }}>{impedimentoAnexo}</span>
                                        </div>
                                    )}
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn"
                                        onClick={fecharAnexo} disabled={enviandoAnexo}>Cancelar</button>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary"
                                        disabled={enviandoAnexo || !!impedimentoAnexo}
                                        title={impedimentoAnexo || 'Anexar o arquivo ao evento'}
                                        onClick={enviarAnexo}>
                                        {enviandoAnexo ? 'Enviando…' : 'Anexar'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal — confirmar a remoção do anexo.
                    Remover tira o arquivo da lista da ND; o binário continua
                    guardado (o backend faz soft delete, não apaga do storage). */}
                {anexoRemover && (
                    <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={function (e) { if (e.target === e.currentTarget) { setAnexoRemover(null); } }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic is-danger"><FaTrash size={17} /></span>
                                    <h3 className="fin-pp__modal-title">Remover da lista da ND
                                        <span className="fin-pp__modal-sub">{anexoRemover.nome}</span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close"
                                        onClick={function () { setAnexoRemover(null); }}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <div className="fin-pp__notice" style={{ margin: '0 0 12px' }}>
                                        <span className="fin-pp__pill is-info" style={{ flex: 'none' }}>
                                            {anexoRemover.tipo_rotulo || anexoRemover.tipo}
                                        </span>
                                        <span style={{ flex: 1, minWidth: 0 }}>
                                            {anexoRemover.descricao || 'sem descrição'}
                                        </span>
                                    </div>
                                    <p style={{ margin: 0 }}>
                                        Este arquivo deixa de compor o PDF da ND. O arquivo em si continua
                                        guardado — só o vínculo com o evento sai da lista.
                                    </p>
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn"
                                        onClick={function () { setAnexoRemover(null); }}
                                        disabled={removendoAnexo}>Cancelar</button>
                                    <button type="button" className="fin-pp__btn fin-pp__btn--danger"
                                        disabled={removendoAnexo} onClick={removerAnexo}>
                                        {removendoAnexo ? 'Removendo…' : 'Remover'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Modal — o que ficou de fora do PDF da ND.
                    AVISO, não erro: o PDF foi gerado e já está no documento. O
                    backend não inventa dado nenhum — o campo sai marcado na
                    carta e volta aqui pelo nome, para ela completar o cadastro
                    e gerar de novo. Não trava: hoje a ND sai à mão com campo em
                    branco do mesmo jeito; o que não pode é sair com um número
                    que o sistema chutou.

                    Duas listas SEPARADAS, e nesta ordem: primeiro o ANEXO que
                    não entrou no PDF (ela ainda vai ter de juntar à mão, e é o
                    que muda o que o cliente recebe), depois o CAMPO em branco
                    da carta. Uma lista só faria ela procurar no lugar errado. */}
                {pdfFaltando && (
                    <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={function (e) { if (e.target === e.currentTarget) { setPdfFaltando(null); } }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    {/* Sem modificador: `.fin-pp__modal-head-ic` só tem
                                        `is-ok` e `is-danger` no design system, e isto
                                        não é erro. O aviso vem do triângulo e das
                                        pills "Falta" do corpo. */}
                                    <span className="fin-pp__modal-head-ic">
                                        <FaExclamationTriangle size={17} />
                                    </span>
                                    <h3 className="fin-pp__modal-title">Confira antes de enviar
                                        <span className="fin-pp__modal-sub">{pdfFaltando.rotulo}</span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close"
                                        onClick={function () { setPdfFaltando(null); }}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    <p style={{ margin: '0 0 12px' }}>
                                        O PDF foi gerado e já está no documento
                                        {pdfFaltando.paginas > 0
                                            ? ' com ' + plural(pdfFaltando.paginas, 'página', 'páginas')
                                            : ''}, mas confira antes de enviar:
                                    </p>

                                    {pdfFaltando.ignorados.length > 0 && (
                                        <div style={{ margin: '0 0 14px' }}>
                                            <div className="fin-pp__cell-strong" style={{ marginBottom: 8 }}>
                                                {plural(pdfFaltando.ignorados.length, 'anexo não entrou', 'anexos não entraram')}
                                                {' '}no PDF — junte à mão ou resolva o arquivo:
                                            </div>
                                            {pdfFaltando.ignorados.map(function (item, i) {
                                                // `alignItems` no topo: o item tem duas linhas (nome e
                                                // motivo) e o `.fin-pp__notice` centraliza por padrão, o
                                                // que deixaria a pill flutuando no meio do bloco.
                                                return (
                                                    <div className="fin-pp__notice" key={'ig' + i}
                                                        style={{ margin: '0 0 8px', alignItems: 'flex-start' }}>
                                                        <span className="fin-pp__pill is-danger" style={{ flex: 'none' }}>Fora</span>
                                                        <span style={{ flex: 1, minWidth: 0 }}>
                                                            <span className="fin-pp__cell-strong">{item.nome}</span>
                                                            <div className="fin-pp__cell-muted" style={{ marginTop: 2 }}>{item.motivo}</div>
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    {pdfFaltando.itens.length > 0 && (
                                        <div>
                                            <div className="fin-pp__cell-strong" style={{ marginBottom: 8 }}>
                                                {plural(pdfFaltando.itens.length, 'campo saiu', 'campos saíram')}
                                                {' '}em branco na carta:
                                            </div>
                                            {pdfFaltando.itens.map(function (item, i) {
                                                return (
                                                    <div className="fin-pp__notice" key={i} style={{ margin: '0 0 8px' }}>
                                                        <span className="fin-pp__pill is-warn" style={{ flex: 'none' }}>Falta</span>
                                                        <span style={{ flex: 1, minWidth: 0 }}>{item}</span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    )}

                                    <p className="fin-pp__cell-muted" style={{ margin: '12px 0 0' }}>
                                        Resolva o que faltou e gere o PDF de novo — a nova versão
                                        substitui esta.
                                    </p>
                                </div>
                                <div className="fin-pp__modal-foot">
                                    <button type="button" className="fin-pp__btn fin-pp__btn--primary"
                                        onClick={function () { setPdfFaltando(null); }}>Entendi</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

            </div>
        </ErpTemplate>
    );
}
